import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { Loader2, Lock, ShieldAlert, Stethoscope } from 'lucide-react';
import { Card } from './index';
import { getVerzuimIntelligence, type VerzuimIntelligenceGroep } from '../services/lopendeTrajectenApi';

/**
 * Menu 4 (DATA): Verzuim-Intelligence uit de Poortwachter-cases.
 * Gevoed door de Ziekmeldings-Wizard (vestiging/afdeling = Toegangs-Scope).
 * Alleen tellingen en dagen — geen namen, hashes of medische inhoud.
 *
 *   KPI-tegels        open cases, klok-verdeling, liability, gem. duur, herhaald verzuim
 *   Per afdeling      gestapelde balk 🟢/🟡/🔴 + langdurig (> 6 weken) + risico
 *   Maandtrend        nieuwe ziekmeldingen vs herstelmeldingen, 12 maanden
 */

const euro = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const KLEUR = { groen: '#16a34a', geel: '#eab308', rood: '#dc2626', nieuw: '#12284A', hersteld: '#16a34a' };

const Tegel: React.FC<{ label: string; value: string | number; sub?: string; tone?: 'rood' | 'geel' | 'groen' | 'neutraal' }> = ({ label, value, sub, tone = 'neutraal' }) => {
  const kleur = { rood: 'text-red-700', geel: 'text-yellow-700', groen: 'text-green-700', neutraal: 'text-gray-900' }[tone];
  return (
    <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-xl font-bold ${kleur}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-500">{sub}</div>}
    </div>
  );
};

const AfdelingBalk: React.FC<{ g: VerzuimIntelligenceGroep; max: number }> = ({ g, max }) => {
  const { t } = useTranslation('employer');
  const breedte = (n: number) => `${max ? (n / max) * 100 : 0}%`;
  const naam = `${g.vestiging ?? t('verzuimIntelligence.alleVestigingen')} ➔ ${g.afdeling ?? '—'}`;
  return (
    <li className="grid grid-cols-12 gap-2 items-center text-xs">
      <div className="col-span-12 md:col-span-4 truncate text-gray-800" title={naam}>{naam}</div>
      <div className="col-span-8 md:col-span-5">
        <div className="h-4 w-full rounded bg-gray-100 overflow-hidden flex" role="img" aria-label={t('verzuimIntelligence.balkAria', { naam, groen: g.groen, geel: g.geel, rood: g.rood })}>
          <div style={{ width: breedte(g.groen), background: KLEUR.groen }} />
          <div style={{ width: breedte(g.geel), background: KLEUR.geel }} />
          <div style={{ width: breedte(g.rood), background: KLEUR.rood }} />
        </div>
      </div>
      <div className="col-span-4 md:col-span-3 text-right text-gray-600 whitespace-nowrap">
        <Trans t={t} i18nKey="verzuimIntelligence.balkOpen" values={{ n: g.open }} components={{ b: <span className="font-semibold text-gray-900" /> }} />
        {g.langdurig > 0 && <span className="ml-1 text-orange-700">{t('verzuimIntelligence.balkLangdurig', { n: g.langdurig })}</span>}
        {g.risico_bedrag > 0 && <span className="ml-1 text-red-700 font-semibold">· {euro.format(g.risico_bedrag)}</span>}
      </div>
    </li>
  );
};

const Maandgrafiek: React.FC<{ maanden: Array<{ maand: string; nieuw: number; hersteld: number }> }> = ({ maanden }) => {
  const { t } = useTranslation('employer');
  const maandKort = t('verzuimIntelligence.maandKort', { returnObjects: true }) as string[];
  const W = 640;
  const H = 160;
  const padL = 28;
  const padB = 22;
  const padT = 10;
  const max = Math.max(1, ...maanden.flatMap((m) => [m.nieuw, m.hersteld]));
  const slot = (W - padL) / maanden.length;
  const bar = Math.max(4, slot * 0.32);
  const y = (v: number) => padT + (H - padT - padB) * (1 - v / max);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label={t('verzuimIntelligence.grafiekAria')}>
      {[0, max].map((v) => (
        <g key={v}>
          <line x1={padL} x2={W} y1={y(v)} y2={y(v)} stroke="#e5e7eb" strokeWidth={1} />
          <text x={padL - 4} y={y(v) + 3} fontSize={9} textAnchor="end" fill="#6b7280">{v}</text>
        </g>
      ))}
      {maanden.map((m, i) => {
        const x0 = padL + i * slot + slot / 2;
        const [jaar, mnd] = m.maand.split('-');
        return (
          <g key={m.maand}>
            <rect x={x0 - bar - 1} y={y(m.nieuw)} width={bar} height={H - padB - y(m.nieuw)} fill={KLEUR.nieuw}>
              <title>{t('verzuimIntelligence.titleNieuw', { maand: maandKort[Number(mnd) - 1], jaar, n: m.nieuw })}</title>
            </rect>
            <rect x={x0 + 1} y={y(m.hersteld)} width={bar} height={H - padB - y(m.hersteld)} fill={KLEUR.hersteld}>
              <title>{t('verzuimIntelligence.titleHersteld', { maand: maandKort[Number(mnd) - 1], jaar, n: m.hersteld })}</title>
            </rect>
            <text x={x0} y={H - 6} fontSize={9} textAnchor="middle" fill="#6b7280">{maandKort[Number(mnd) - 1]}</text>
          </g>
        );
      })}
    </svg>
  );
};

export const VerzuimIntelligence: React.FC = () => {
  const { t } = useTranslation('employer');
  const { data, isLoading, isError } = useQuery({ queryKey: ['lopende-trajecten', 'poortwachter', 'intelligence'], queryFn: getVerzuimIntelligence, staleTime: 60_000 });
  return (
    <Card>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Stethoscope className="w-5 h-5 text-red-600" /> {t('verzuimIntelligence.titel')}</h2>
          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Lock className="w-3 h-3" /> {t('verzuimIntelligence.sub')}</p>
        </div>
        <Link to="/employer/lopende-trajecten?tab=poortwachter" className="text-sm text-blue-700 hover:underline">{t('verzuimIntelligence.pipelineLink')}</Link>
      </div>
      {isLoading && <div className="flex items-center justify-center py-8 text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('verzuimIntelligence.laden')}</div>}
      {isError && <p className="text-sm text-red-600">{t('verzuimIntelligence.fout')}</p>}
      {data && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <Tegel label={t('verzuimIntelligence.tegelOpen')} value={data.totaal.open} sub={t('verzuimIntelligence.tegelOpenSub', { n: data.totaal.gesloten_12m })} />
            <Tegel label={t('verzuimIntelligence.tegelKlok')} value={`${data.totaal.groen} · ${data.totaal.geel} · ${data.totaal.rood}`} sub={t('verzuimIntelligence.tegelKlokSub')} tone={data.totaal.rood ? 'rood' : data.totaal.geel ? 'geel' : 'groen'} />
            <Tegel label={t('verzuimIntelligence.tegelLiability')} value={euro.format(data.totaal.risico_bedrag)} sub={data.fte == null ? t('verzuimIntelligence.tegelFteOnbekend') : t('verzuimIntelligence.tegelFte', { n: data.fte })} tone={data.totaal.risico_bedrag ? 'rood' : 'groen'} />
            <Tegel label={t('verzuimIntelligence.tegelDuur')} value={data.totaal.gem_verzuimduur == null ? '—' : t('verzuimIntelligence.tegelDuurWaarde', { n: data.totaal.gem_verzuimduur })} sub={t('verzuimIntelligence.tegelDuurSub', { n: data.totaal.langdurig })} tone={data.totaal.langdurig ? 'geel' : 'neutraal'} />
            <Tegel label={t('verzuimIntelligence.tegelHerhaald')} value={data.totaal.herhaald_verzuim} sub={t('verzuimIntelligence.tegelHerhaaldSub')} tone={data.totaal.herhaald_verzuim ? 'geel' : 'neutraal'} />
            <Tegel label={t('verzuimIntelligence.tegelAfdelingen')} value={data.afdelingen.filter((a) => a.open > 0).length} sub={t('verzuimIntelligence.tegelAfdelingenSub', { n: data.afdelingen.length })} />
          </div>

          {data.totaal.risico_bedrag > 0 && (
            <div className="p-3 rounded-lg bg-red-600 text-white text-sm font-semibold flex items-center gap-2">
              <ShieldAlert className="w-4 h-4" /> {t('verzuimIntelligence.criticalBanner', { n: data.totaal.rood, bedrag: euro.format(data.totaal.risico_bedrag) })}
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">{t('verzuimIntelligence.perAfdelingTitel')}</h3>
              {data.afdelingen.filter((a) => a.open > 0).length === 0 ? (
                <p className="text-sm text-gray-500">{t('verzuimIntelligence.geenOpen')}</p>
              ) : (
                <ul className="space-y-2">
                  {data.afdelingen.filter((a) => a.open > 0).map((g) => (
                    <AfdelingBalk key={`${g.vestiging}|${g.afdeling}`} g={g} max={Math.max(...data.afdelingen.map((a) => a.open))} />
                  ))}
                </ul>
              )}
              <p className="mt-2 text-[11px] text-gray-500">{t('verzuimIntelligence.balkUitleg')}</p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">{t('verzuimIntelligence.trendTitel')}</h3>
              <Maandgrafiek maanden={data.maanden} />
              <div className="flex gap-4 text-[11px] text-gray-600 mt-1">
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: KLEUR.nieuw }} /> {t('verzuimIntelligence.legendaNieuw')}</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: KLEUR.hersteld }} /> {t('verzuimIntelligence.legendaHersteld')}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default VerzuimIntelligence;
